import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from '../api/axios';
import styles from './PositionsManagement.module.css';
import PositionForm from '../components/PositionForm';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';

interface Position {
  id: number;
  title: string;
  description?: string;
  department: string;
  scopes: string[];
  tier: number;
  rank: number;
  guide?: string;
  requirements?: string;
  commitmentLevel: number;
  isActive: boolean;
  maxHolders?: number | null;
  createdAt: string;
  updatedAt?: string;
}

const PositionsManagement: React.FC = () => {
  const { user } = useAuth();
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [filterTier, setFilterTier] = useState<number | 'all'>('all');
  const [showInactive, setShowInactive] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);

  const departments = [
    { id: 'logistics', name: 'Logistics' },
    { id: 'security', name: 'Security' },
    { id: 'exploration', name: 'Exploration' },
    { id: 'support', name: 'Support' },
    { id: 'intelligence', name: 'Intelligence' },
    { id: 'technology', name: 'Technology' },
    { id: 'operations', name: 'Operations' },
    { id: 'medical', name: 'Medical' },
  ];

  const fetchPositions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/positions?includeInactive=${showInactive}`);
      console.log('Positions API response:', response.data);
      const data = Array.isArray(response.data) ? response.data : [];
      console.log('Processed positions data:', data);
      setPositions(data);
    } catch (error) {
      console.error('Failed to fetch positions:', error);
      setPositions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, [showInactive]);

  // 🔒 Authorization: Admin + CEO only
  const isCeo = (): boolean => {
    if (!user) return false;
    const titles = [
      user.primaryPositionTitle,
      user.secondaryPositionTitle,
      user.tertiaryPositionTitle
    ].filter(Boolean).map((t: string) => t.toLowerCase());
    
    return titles.includes('ceo') || titles.includes('chief executive officer');
  };

  const hasAccess = user?.role === 'Admin' || isCeo();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAccess) {
    return <Navigate to="/home" replace />;
  }

  const handleCreate = () => {
    setEditingPosition(null);
    setShowForm(true);
  };

  const handleEdit = (position: Position) => {
    setEditingPosition(position);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingPosition(null);
    fetchPositions();
  };

  const handleView = (position: Position) => {
    setSelectedPosition(position);
  };

  const handleToggleActive = async (position: Position) => {
    try {
      await axios.put(`/positions/${position.id}`, {
        ...position,
        isActive: !position.isActive
      });
      fetchPositions();
    } catch (error) {
      console.error('Failed to toggle position status:', error);
    }
  };

  const getRankLabel = (rank: number): string => {
    const labels: { [key: number]: string } = {
      1: 'Initiate',
      2: 'Intermediate',
      3: 'Experienced',
      4: 'Expert'
    };
    return labels[rank] || 'Unknown';
  };

  const getTierLabel = (tier: number): string => {
    const labels: { [key: number]: string } = {
      1: 'Entry Level',
      2: 'Intermediate',
      3: 'Senior / Leadership',
      4: 'Executive / C-Suite'
    };
    return labels[tier] || 'Unknown';
  };

  const getCommitmentLabel = (level: number): string => {
    const labels: { [key: number]: string } = {
      0: 'Casual',
      1: 'Casual',
      2: 'Regular',
      3: 'Dedicated',
      4: 'Full Time'
    };
    return labels[level] || 'Not specified';
  };

  const getCommitmentColor = (level: number): string => {
    const colors: { [key: number]: string } = {
      0: '#6b7280',
      1: '#6b7280',
      2: '#3b82f6',
      3: '#8b5cf6',
      4: '#dc2626'
    };
    return colors[level] || '#6b7280';
  };

  const getDepartmentName = (deptId: string): string => {
    return departments.find(d => d.id === deptId)?.name || deptId;
  };

  const filteredPositions = positions.filter((pos) => {
    const matchesSearch = pos.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pos.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = filterDepartment === 'all' || pos.department === filterDepartment;
    const matchesTier = filterTier === 'all' || pos.tier === filterTier;
    return matchesSearch && matchesDepartment && matchesTier;
  });

  if (loading) {
    return (
      <div className={styles.dashboardContainer}>
        <div className={styles.content}>
          <div className={styles.loading}>Loading positions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.content}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.pageTitle}>Position Management</h1>
            <p className={styles.pageDescription}>
              Manage positions across all departments
            </p>
          </div>
          <button className={styles.createButton} onClick={handleCreate}>
            ➕ Create Position
          </button>
        </div>

        <div className={styles.controls}>
          <input
            type="text"
            placeholder="Search positions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />

          <select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Departments</option>
            {departments.map(dept => (
              <option key={dept.id} value={dept.id}>{dept.name}</option>
            ))}
          </select>

          <select
            value={filterTier}
            onChange={(e) => setFilterTier(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className={styles.filterSelect}
          >
            <option value="all">All Tiers</option>
            <option value="1">Tier 1 - Entry Level</option>
            <option value="2">Tier 2 - Intermediate</option>
            <option value="3">Tier 3 - Senior</option>
            <option value="4">Tier 4 - Executive</option>
          </select>

          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show Archived
          </label>
        </div>

        <div className={styles.stats}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{positions.filter(p => p.isActive).length}</div>
            <div className={styles.statLabel}>Active Positions</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{filteredPositions.length}</div>
            <div className={styles.statLabel}>Filtered Results</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{positions.filter(p => !p.isActive).length}</div>
            <div className={styles.statLabel}>Archived</div>
          </div>
        </div>

        {filteredPositions.length === 0 ? (
          <div className={styles.empty}>
            <p>No positions found matching your filters.</p>
            <p>Click "Create Position" to add a new position.</p>
          </div>
        ) : (
          <div className={styles.grid}>
            {filteredPositions.map((position) => (
              <div
                key={position.id}
                className={`${styles.positionCard} ${!position.isActive ? styles.inactive : ''}`}
              >
                {!position.isActive && <div className={styles.archivedBanner}>Archived</div>}
                
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>{position.title}</h3>
                  <span className={`${styles.tier} ${styles[`tier${position.tier}`]}`}>
                    Tier {position.tier}
                  </span>
                </div>

                <div className={styles.departmentBadge}>
                  {getDepartmentName(position.department)}
                </div>

                <div className={styles.positionMeta}>
                  <div className={styles.metaItem}>
                    <strong>Level:</strong> <span>{getTierLabel(position.tier)}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <strong>Experience:</strong> <span>{getRankLabel(position.rank)}</span>
                  </div>
                  <div className={styles.metaItem}>
                    <strong>Commitment:</strong> <span style={{ color: getCommitmentColor(position.commitmentLevel) }}>{getCommitmentLabel(position.commitmentLevel)}</span>
                  </div>
                </div>

                {position.description && (
                  <p className={styles.description}>
                    {position.description.length > 120 
                      ? `${position.description.substring(0, 120)}...` 
                      : position.description}
                  </p>
                )}

                <div className={styles.cardActions}>
                  <button 
                    className={styles.viewButton}
                    onClick={() => handleView(position)}
                  >
                    View Details
                  </button>
                  {(user?.role === 'Admin' || position.tier !== 4) && (
                    <>
                      <button 
                        className={styles.editButton}
                        onClick={() => handleEdit(position)}
                      >
                        Edit
                      </button>
                      <button 
                        className={position.isActive ? styles.archiveButton : styles.restoreButton}
                        onClick={() => handleToggleActive(position)}
                      >
                        {position.isActive ? 'Archive' : 'Restore'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {showForm && (
          <Modal onClose={handleFormClose}>
            <PositionForm
              position={editingPosition}
              department={editingPosition?.department || ''}
              onClose={handleFormClose}
            />
          </Modal>
        )}

        {selectedPosition && (
          <Modal onClose={() => setSelectedPosition(null)}>
            <div style={{ padding: '20px' }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px', color: 'var(--text-primary)' }}>
                {selectedPosition.title}
              </h2>
              <div className={styles.detailsModal}>
              <div className={styles.detailSection}>
                <h4>Department</h4>
                <p>{getDepartmentName(selectedPosition.department)}</p>
              </div>

              <div className={styles.detailSection}>
                <h4>Description</h4>
                <p>{selectedPosition.description || 'No description provided'}</p>
              </div>

              <div className={styles.detailSection}>
                <h4>Details</h4>
                <div className={styles.detailGrid}>
                  <div><strong>Tier:</strong> {getTierLabel(selectedPosition.tier)}</div>
                  <div><strong>Rank:</strong> {getRankLabel(selectedPosition.rank)}</div>
                  <div><strong>Commitment:</strong> {getCommitmentLabel(selectedPosition.commitmentLevel)}</div>
                  <div><strong>Status:</strong> {selectedPosition.isActive ? 'Active' : 'Archived'}</div>
                </div>
              </div>

              {selectedPosition.requirements && (
                <div className={styles.detailSection}>
                  <h4>Requirements</h4>
                  <p>{selectedPosition.requirements}</p>
                </div>
              )}

              {selectedPosition.guide && (
                <div className={styles.detailSection}>
                  <h4>Guide</h4>
                  <div dangerouslySetInnerHTML={{ __html: selectedPosition.guide }} />
                </div>
              )}

              {selectedPosition.scopes && selectedPosition.scopes.length > 0 && (
                <div className={styles.detailSection}>
                  <h4>Permissions</h4>
                  <div className={styles.scopesList}>
                    {selectedPosition.scopes.map((scope, idx) => (
                      <span key={idx} className={styles.scopeTag}>{scope}</span>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default PositionsManagement;
